import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

const TITLE_SIZE = {
  md: 'text-h-section',
  card: 'text-h-m',
  sm: 'text-h-s',
} as const

interface SectionHeadProps {
  /** The section's name. */
  title?: ReactNode
  /**
   * The quiet line above the title: "After the close, 2 Bhadra 2083". Without a title it is the
   * kicker that names a table or a strip from outside its card, and becomes the heading itself.
   */
  eyebrow?: ReactNode
  /** Beside the title: "7 names", a list picker. */
  meta?: ReactNode
  /** The right-hand slot, a `SeeAllLink` as a rule. */
  action?: ReactNode
  /**
   * Makes the title a link to the section's own page, as the mock's Market heads are ("Indices",
   * "Top brokers today"): it dims to .72 on hover. Usually the same page as the action's.
   */
  titleTo?: NonNullable<LinkProps['to']> | undefined
  titleSearch?: LinkProps['search'] | undefined
  /** `h3` for a section inside another titled one. Default `h2`, under the page's `h1`. */
  as?: 'h2' | 'h3' | undefined
  /**
   * Default `md`, the 20px section rung. `card` is a boxed card's own head at `h-m` (16), one
   * step under the section it sits in, on a row at least 36px tall — the height of the inner
   * `TabBar` or `SegmentedControl` its right slot may hold, so a head with one and a head without
   * stand the same; SectionCard picks it. `sm` sets the title at `h-s` (14) for a head inside a
   * card's own body — a breadth bar, a range row — under the card's 16.
   */
  size?: 'md' | 'card' | 'sm' | undefined
  className?: string | undefined
}

/**
 * The head of a section: its name at 20px, an optional eyebrow above, and "See all ▸" at the
 * right, level with the name rather than with the eyebrow. SectionCard draws its own head through
 * this, inside the card or above it; a flat Home section or a table uses it directly.
 *
 * One rung between the page title (32) and a card's own title (16), so a page reads
 * 32 → 20 → 16 → 14 by size as well as by heading level. The eyebrow is 12/600 in the subtle
 * grey: alone it names a card the way the reference's card heads do, a label rather than a title.
 */
export default function SectionHead({
  title,
  eyebrow,
  meta,
  action,
  titleTo,
  titleSearch,
  as: Heading = 'h2',
  size = 'md',
  className,
}: SectionHeadProps) {
  const hasTitleRow = title !== undefined || meta !== undefined
  // With no title the eyebrow IS the section's name, so it must be the heading — a <p> would
  // leave the section invisible to anyone navigating by heading.
  const EyebrowTag = title === undefined ? Heading : 'p'
  const eyebrowNode = eyebrow ? (
    <EyebrowTag className="text-h-xs text-muted-foreground-subtle">{eyebrow}</EyebrowTag>
  ) : null

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {hasTitleRow ? eyebrowNode : null}
      {/* In a phone-narrow container (the page column, a card's own head box) the action drops
          under the title once the two no longer fit side by side, rather than squeezing the title
          to three lines beside an inner `TabBar`. `basis-auto` is what lets that happen: at
          `flex-1`'s zero basis the title always "fits" and never wraps the action. Wider than
          that, the row is unchanged: the title takes the room the action leaves. */}
      <div
        className={cn(
          'flex items-center gap-3 @max-md:flex-wrap',
          size === 'card' ? 'min-h-9' : null,
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 @max-md:basis-auto">
          {hasTitleRow ? null : eyebrowNode}
          {title ? (
            <Heading className={cn('text-foreground', TITLE_SIZE[size])}>
              {titleTo === undefined ? (
                title
              ) : (
                <Link
                  to={titleTo}
                  {...(titleSearch === undefined ? {} : { search: titleSearch })}
                  className="rounded-xs transition-opacity hover:opacity-72 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {title}
                </Link>
              )}
            </Heading>
          ) : null}
          {meta ? (
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-body-xs text-muted-foreground">
              {meta}
            </div>
          ) : null}
        </div>
        {action ? <div className="shrink-0 @max-md:min-w-0 @max-md:shrink">{action}</div> : null}
      </div>
    </div>
  )
}
